import clsx from "clsx";
import { motion, useReducedMotion } from "framer-motion";
import { useMemo } from "react";
import CurrencyFlag from "react-currency-flags";

import { getTotalByTransactionType } from "src/helpers/utils";
import useMobile from "src/hooks/useMobile";
import { ETransactionType } from "src/helpers/constants";
import useAllTransactions from "src/hooks/useAllTransactions";
import useCurrencies from "src/hooks/useCurrencies";
import CurrencyBadge from "../CurrencyBadge/CurrencyBadge";

interface ITransactionsPerCurrencyCard {
  currency: string;
  accounts: any[];
  animationIndex: number;
  onClick?: () => void;
  active: boolean;
}

const TransactionsPerCurrencyCard: React.FC<ITransactionsPerCurrencyCard> = ({
  currency,
  accounts,
  animationIndex,
  onClick,
  active,
}) => {
  const { isMobile } = useMobile();
  const { getCurrencyDataByCode } = useCurrencies();
  const { allActiveTransactions, isLoadingAllTransactions } =
    useAllTransactions();
  const prefersReducedMotion = useReducedMotion();

  const animationDirection = useMemo(() => {
    return isMobile ? "y" : "x";
  }, [isMobile]);

  const totalsPerCurrency = useMemo(() => {
    const totalIncome = (accounts || []).reduce((sum: number, account: any) => {
      const income = getTotalByTransactionType({
        type: ETransactionType.INCOME,
        transactions: allActiveTransactions,
        account: account?.account,
      });

      return sum + (Number(income) || 0);
    }, 0);

    const totalExpenses = (accounts || []).reduce(
      (sum: number, account: any) => {
        const expenses = getTotalByTransactionType({
          type: ETransactionType.EXPENSE,
          transactions: allActiveTransactions,
          account: account?.account,
        });

        return sum + (Number(expenses) || 0);
      },
      0,
    );

    return {
      currency,
      accounts,
      totalIncome,
      totalExpenses,
      balance: totalIncome - totalExpenses,
    };
  }, [accounts, currency, allActiveTransactions]);

  const currencyInfo = useMemo(() => {
    return getCurrencyDataByCode({ code: currency });
  }, [currency]);

  return (
    <motion.div
      className={clsx([
        "flex",
        "w-full",
        "group",
        // "md:flex-row",
        "flex-col",
        "rounded",
        "p-2",
        "md:p-2",
        "md:gap-1",
        "gap-0",
        // "bg-gray-50/50 dark:bg-gray-900/50",
        "hover:bg-gray-100",
        "hover:dark:bg-gray-900",
        // "transition-opacity",
        {
          "cursor-pointer": onClick,
        },
        active && ["bg-gray-50", "dark:bg-gray-700"],
        "border",
        "border-gray-50",
        "dark:border-gray-900/50",
      ])}
      {...(!prefersReducedMotion && {
        initial: {
          opacity: 0,
          x: animationIndex % 2 === 0 ? -100 : 100,
        },
        animate: { opacity: 1, x: 0 },
        transition: {
          duration: 0.2,
          ease: "easeOut",
          delay: animationIndex * 0.1,
        },
      })}
      onClick={() => {
        if (onClick) onClick();
      }}
    >
      <div
        className={clsx([
          "flex",
          "flex-row",
          "gap-1",
          "items-center",
          "truncate",
        ])}
      >
        <div className="shrink-0">
          <CurrencyFlag currency={currency} size={isMobile ? "sm" : "sm"} />
        </div>
        <div
          className={clsx(["text-xs", "truncate", "md:text-xs", "font-medium"])}
        >
          {currencyInfo?.name}
        </div>
      </div>
      <div
        className={clsx([
          "flex",
          "flex-col",
          "w-full",
          "p-0",
          "md:p-0",
          "rounded",
          "md:gap-4",
          "gap-2",
        ])}
      >
        <div
          className={clsx([
            "flex",
            "flex-row",
            "gap-2",
            "items-center",
            "w-full",
          ])}
        >
          <div
            className={clsx([
              "flex",
              "flex-col",
              "truncate",
              "flex-grow",
              // "transition-opacity",
              "gap-1",
              "items-start",
              "md:items-start",
            ])}
          >
            <CurrencyBadge
              value={totalsPerCurrency.totalIncome}
              currencySymbol={currencyInfo?.symbol_native}
              kind={ETransactionType.INCOME}
            />
            <CurrencyBadge
              value={totalsPerCurrency.totalExpenses}
              currencySymbol={currencyInfo?.symbol_native}
              kind={ETransactionType.EXPENSE}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TransactionsPerCurrencyCard;
